import { FC, useCallback, useLayoutEffect } from 'react';
import { MutationFunction, useMutation, useQuery } from '@tanstack/react-query';
import { BigNumber } from '@ethersproject/bignumber';
import useSiloRouter from '../../../../hooks/useSiloRouter';
import { SiloAsset } from 'queries/decoratedSilo';
import { fetchAllowance, parseAllowance, useAddTransaction, useSiloWeb3 } from '@silo/web3';
import { ContractReceipt } from 'ethers';
import { isNull, isUndefined } from '@silo/utils';
import { useThisDecoratedSilo } from '../../silo';
import { usePostMutationUpdate } from '@silo/query';
import { useQueryTermsOfUseAgreement } from 'app/queries/terms-of-use';
import { TermsOfUseModal } from '../../terms-of-use';
import { ProtocolInstance, isSupportedChainId } from '@silo/types';
import { Button, Dots, Tooltip, TooltipContent, TooltipOrigin, useBreakpointValue, useModalState } from '@silo/ui';

export interface ApprovalProps {
  selectedAsset: SiloAsset;
  queryApproval: QueryApproval;
  doApprove: DoApprove;
  normalizedTokenAmount: BigNumber | null;
  setPreventSubmit: (value: boolean) => void;
  hide?: boolean;
  protocolInstance: ProtocolInstance;
}

export const Approval: FC<ApprovalProps> = ({
  selectedAsset,
  queryApproval,
  doApprove,
  hide = false,
  normalizedTokenAmount,
  setPreventSubmit,
  protocolInstance,
}) => {
  const approvalState = useApprovalState({
    selectedAsset,
    queryApproval,
    doApprove,
    amount: normalizedTokenAmount?.toBigInt() ?? 0n,
  });

  /**
   * Setting prevent submit will disable the submit button in the action form.
   * There are also cases where the approval flow will bump the submit button
   * down. To prevent a flickering of the submit button going from enabled to
   * disabled, useLayoutEffect is used.
   */
  useLayoutEffect(() => {
    /**
     * Only allow a submission if the user has an appropriate allowance
     */
    const preventSubmit = approvalState._tag !== 'approved';
    setPreventSubmit(preventSubmit);
  }, [approvalState, setPreventSubmit]);

  if (hide) return <></>;

  return (
    <ApproveButton approvalState={approvalState} selectedAsset={selectedAsset} protocolInstance={protocolInstance} />
  );
};

export type QueryApproval = (asset: SiloAsset) => Promise<bigint>;

export const useQueryApproval = (): QueryApproval => {
  const { clientProvider, account } = useSiloWeb3();
  const { routerAddress } = useThisDecoratedSilo();

  return useCallback<QueryApproval>(
    async ({ userBalanceTokenAddress }) => {
      if (isUndefined(clientProvider) || isUndefined(account)) {
        throw new Error('Undefined provider or account');
      }

      return fetchAllowance({
        clientProvider,
        account,
        assetAddress: userBalanceTokenAddress,
        spender: routerAddress,
      });
    },
    [clientProvider, account, routerAddress]
  );
};

interface DoApproveVariables {
  asset: SiloAsset;
}
export type DoApprove = MutationFunction<ContractReceipt, DoApproveVariables>;

export const useDoApprove = (): DoApprove => {
  const { approveRouterAssetSpend } = useSiloRouter();
  const addTransaction = useAddTransaction();
  return useCallback(
    async ({ asset: { userBalanceTokenAddress, symbol } }: DoApproveVariables) => {
      const tx = await approveRouterAssetSpend(userBalanceTokenAddress);
      addTransaction(tx, `Approve ${symbol}`);

      const receipt = await tx.wait();

      if (receipt.status === 0) {
        throw new Error(`Transaction ${tx.hash} reverted`);
      }

      return receipt;
    },
    [approveRouterAssetSpend, addTransaction]
  );
};

interface UseApprovalStateProps {
  selectedAsset: SiloAsset;
  amount: bigint;
  queryApproval: QueryApproval;
  doApprove: DoApprove;
}

type ApprovalState =
  | { _tag: 'unknown' }
  | { _tag: 'noAllowance'; approve: () => void }
  | { _tag: 'insufficientAllowance'; approve: () => void; allowance: bigint }
  | { _tag: 'approved' }
  | { _tag: 'pending' };

export const approvalKey = 'approval';

const useApprovalState = ({
  selectedAsset,
  queryApproval,
  doApprove,
  amount,
}: UseApprovalStateProps): ApprovalState => {
  const { assetAddress } = selectedAsset;
  const { account, chainId } = useSiloWeb3();
  const postMutationUpdate = usePostMutationUpdate();
  const mutation = useMutation<ContractReceipt, Error, DoApproveVariables>(doApprove, {
    onSuccess: async (receipt) => {
      const parsedAllowance = parseAllowance(receipt);
      const queryKey = [approvalKey, account, assetAddress];

      if (isNull(parsedAllowance)) {
        await query.refetch();
      } else {
        if (!isSupportedChainId(chainId)) return;

        postMutationUpdate({
          queryKey,
          value: parsedAllowance,
          chainId,
        });
      }
    },
  });

  const query = useQuery<bigint, Error>([approvalKey, account, assetAddress], () => queryApproval(selectedAsset));
  const allowance = query.data;

  const approve = (): void => {
    mutation.mutate({ asset: selectedAsset });
  };

  if (selectedAsset.isNative) return { _tag: 'approved' };

  if (mutation.isLoading) return { _tag: 'pending' };

  if (isUndefined(allowance)) {
    return { _tag: 'unknown' };
  }

  if (allowance === 0n) {
    return { _tag: 'noAllowance', approve };
  }

  if (allowance < amount) {
    return { _tag: 'insufficientAllowance', approve, allowance };
  }

  return { _tag: 'approved' };
};

export interface ApproveButtonProps {
  approvalState: ApprovalState;
  selectedAsset: SiloAsset;
  protocolInstance: ProtocolInstance;
}

export const ApproveButton: FC<ApproveButtonProps> = ({ approvalState, selectedAsset, protocolInstance }) => {
  switch (approvalState._tag) {
    case 'unknown':
    case 'approved':
    default:
      return <></>;
    case 'noAllowance':
    case 'insufficientAllowance': {
      const { approve } = approvalState;

      return <NeedsAllowance approve={approve} selectedAsset={selectedAsset} protocolInstance={protocolInstance} />;
    }
    case 'pending':
      return (
        <Button variant="primary" className="w-full" disabled>
          <Dots>Approving</Dots>
        </Button>
      );
  }
};

interface NeedsAllowanceProps {
  approve: () => void;
  selectedAsset: SiloAsset;
  protocolInstance: ProtocolInstance;
}

function NeedsAllowance({ approve, selectedAsset, protocolInstance }: NeedsAllowanceProps) {
  const { account } = useSiloWeb3();
  const { hasAgreed } = useQueryTermsOfUseAgreement();
  const { isModalOpen, closeModal, openModal } = useModalState();
  const { siloAddress } = useThisDecoratedSilo();

  const isHelpTextTooltip = useBreakpointValue(false, { lg: true });

  const onClick = () => {
    if (hasAgreed) {
      approve();
    } else {
      openModal();
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <Tooltip placement="right" off={!isHelpTextTooltip}>
        <TooltipOrigin className="w-full">
          <Button variant="primary" className="w-full" onClick={onClick} data-cy="approval-button">
            Allow Silo to use your {selectedAsset.symbol}
          </Button>
        </TooltipOrigin>
        <TooltipContent>
          You must give the Silo smart contracts permission to use your {selectedAsset.symbol}.
        </TooltipContent>
      </Tooltip>

      {!isHelpTextTooltip && (
        <p className="text-sm text-shale-200">
          You must give the Silo smart contracts permission to use your {selectedAsset.symbol}.
        </p>
      )}

      {!!account && (
        <TermsOfUseModal
          isModalOpen={isModalOpen}
          closeModal={closeModal}
          user={account}
          onSuccess={() => approve()}
          market={siloAddress}
          action="approve"
          protocolInstance={protocolInstance}
        />
      )}
    </div>
  );
}
